import { createContext, useContext, useState, useEffect } from "react";
import { userRepository } from "../../../di/di";
import { useNavigate, useLocation } from "react-router-dom";
import { DashboardRoute } from "../../../../../core/routes/route_constants";
import { toast } from "sonner";
import useToast from "../../../../hooks/useToast";
import TrackingService from "../../../../data/service/impl/tracking_service_impl";
import { useDispatch } from "react-redux";
import { getUser } from "../../../state/slice/user_slice";

export const LoginV2Context = createContext();

export const LoginV2ContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
    const [verifying, setVerifying] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);

    const trackingService = new TrackingService();
    useEffect(() => {
        let interval;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    // Get redirect param from query string
    const searchParams = new URLSearchParams(location.search);
    const redirectPath = searchParams.get('redirect');
    const decodedRedirectPath = redirectPath ? decodeURIComponent(redirectPath) : null;

    function navigateWithHashSupport(path) {
        if (path && path.includes('--hash--')) {
            const [base, hash] = path.split('--hash--');
            navigate(`${base}#${hash}`, { replace: true });
        } else if (path) {
            navigate(path, { replace: true });
        } else {
            navigate(DashboardRoute);
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage('');

        const response = await userRepository.login(email, password, rememberMe);

        if (!response.success) {
            setIsLoading(false);

            if (response.message == 'User not verified') {
                setShowOtpModal(true);
                return;
            }
            setErrorMessage(response.message);
            return;
        }

        // Fetch user and wait for Redux state to update before navigating
        try {
            await dispatch(getUser()).unwrap();
            navigateWithHashSupport(decodedRedirectPath);
            trackingService.identify(response.id, {
                distinct_id: response.id,
                timestamp: new Date().toISOString()
            });
        } catch (err) {
            setErrorMessage('Failed to fetch user info after login.');
        } finally {
            setIsLoading(false);
        }
    };

    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const toggleRememberMe = (e) => setRememberMe(e.target.checked);
    const handleEmailChange = (e) => setEmail(e.target.value);
    const handlePasswordChange = (e) => setPassword(e.target.value);

    const handleOtpChange = (index, value) => {
        // Update the OTP code at the specified index
        const newOtp = [...otpCode];
        newOtp[index] = value;
        setOtpCode(newOtp);

        // Auto-focus next input if value is entered
        if (value !== '' && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            if (nextInput) nextInput.focus();
        }
        else if (value === '' && index > 0) {
            const nextInput = document.getElementById(`otp-${index - 1}`);
            if (nextInput) nextInput.focus();
        }
    };

    const handleVerifyOtp = async () => {
        setVerifying(true);
        setErrorMessage('');

        const otp = otpCode.join('');
        const response = await userRepository.validateOtp(email, otp);
        setVerifying(false);

        if (!response.success) {
            setErrorMessage(response.message);
            return;
        }

        const login_response = await userRepository.login(email, password, rememberMe);

        if (!login_response.success) {
            setErrorMessage(login_response.message);
            return;
        }

        // Fetch user and wait for Redux state to update before navigating
        try {
            await dispatch(getUser()).unwrap();
            if (decodedRedirectPath) {
                navigate(decodedRedirectPath, { replace: true });
            } else {
                navigate(DashboardRoute);
            }
        } catch (err) {
            setErrorMessage('Failed to fetch user info after OTP verification.');
        }
    };

    const handleResendOtp = async () => {
        if (resendTimer > 0) return;

        const response = await userRepository.resendOtp(email);
        if (!response.success) {
            setErrorMessage(response.message);
            return;
        }

        setResendTimer(60); // Set 60 seconds timer
        toast.success('OTP sent successfully');
    }

    const contextValue = {
        email,
        password,
        showPassword,
        rememberMe,
        isLoading,
        errorMessage,
        handleSubmit,
        togglePasswordVisibility,
        toggleRememberMe,
        handleEmailChange,
        handlePasswordChange,
        showOtpModal,
        handleOtpChange,
        otpCode,
        verifying,
        handleVerifyOtp,
        handleResendOtp,
        setOtpCode,
        resendTimer
    };

    return <LoginV2Context.Provider value={contextValue}>{children}</LoginV2Context.Provider>
}

export const useLoginV2Context = () => {
    return useContext(LoginV2Context);
}